import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  Route as RouteIcon, Lock, ArrowLeft, AlertTriangle, CheckCircle2, Zap, Truck, CalendarX,
} from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz, formatDateTimeForInput } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { NewLoadDialog } from '../../components/nursery/despatch/NewLoadDialog';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { despatchWhen, addressLine } from '../../components/nursery/despatch/despatchText';
import type { LoadFieldsValue } from '../../components/nursery/despatch/LoadFields';
import {
  DESPATCH_METHOD_LABELS,
  LOAD_TYPE_LABELS,
  type DespatchOrder,
  type DespatchPlanner as Planner,
  type NurseryDeliveryZone,
} from '../../types/nursery';

/**
 * /nursery/despatch — every open order due in a window, grouped by zone then
 * customer, pulled onto loads from here.
 *
 * The banner is the point of the page: how many orders due in the window are
 * on no load. A bare-root nursery takes orders all year and despatches in one
 * month of winter; this is what stops that month closing with somebody
 * missed. Overdue and undated orders show in every window for the same reason.
 *
 * Plan: docs/nursery-despatch-plan.md, Phase 1.
 */

const PRESETS = [7, 30, 90];

interface Group {
  key: string;
  title: string;
  orders: DespatchOrder[];
}

function groupOrders(orders: DespatchOrder[]): Group[] {
  const map = new Map<string, Group>();
  for (const o of orders) {
    // A cross dock order goes to the dock, not to its store's zone: it is
    // planned by the route and cut-off the schedule gave it.
    const key = o.despatch_method === 'collection' ? 'collection'
      : o.despatch_method === 'cross_dock' ? `xdock:${o.xdock_route ?? ''}`
        : o.delivery_zone_id ? `zone:${o.delivery_zone_id}` : 'nozone';
    const title = key === 'collection' ? 'Collections'
      : o.despatch_method === 'cross_dock' ? `Cross dock · ${o.xdock_route || 'no route'}`
        : o.delivery_zone_name || 'No delivery zone';
    if (!map.has(key)) map.set(key, { key, title, orders: [] });
    map.get(key)!.orders.push(o);
  }
  const rank = (g: Group) => (g.key === 'collection' ? 0 : g.key === 'nozone' ? 3 : g.key.startsWith('xdock:') ? 1 : 2);
  const groups = [...map.values()].sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));
  for (const g of groups) {
    g.orders.sort((a, b) =>
      g.key === 'collection'
        ? (a.collection_at ?? '9').localeCompare(b.collection_at ?? '9')
        : (a.customer_name ?? '').localeCompare(b.customer_name ?? '') || a.order_number.localeCompare(b.order_number),
    );
  }
  return groups;
}

function DespatchPlanner() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(addDays(today, 30));
  const [data, setData] = useState<Planner | null>(null);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnLoad, setShowOnLoad] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [targetLoadId, setTargetLoadId] = useState<number | ''>('');
  const [nowFarmId, setNowFarmId] = useState<number | ''>('');
  const [newLoad, setNewLoad] = useState<LoadFieldsValue | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!from || !to || to < from) return;
    setLoading(true);
    setError(null);
    try {
      const [res, zoneRes] = await Promise.all([
        nurseryService.getDespatchPlanner(from, to),
        nurseryService.getDeliveryZones(),
      ]);
      if (res.success) {
        setData(res.data);
        setSelected(new Set());
        setNowFarmId((f) => f || (res.data.farms.length === 1 ? res.data.farms[0].id : ''));
      } else {
        setError(errorMessage(res, 'Could not load the planner'));
      }
      if (zoneRes.success) setZones(zoneRes.data || []);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the planner'));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const orders = useMemo(() => data?.orders ?? [], [data]);
  const byId = useMemo(() => new Map(orders.map((o) => [o.id, o])), [orders]);

  // Needs to go today: ASAP, or a collection booked for today or earlier.
  const despatchNow = useMemo(
    () => orders.filter((o) => !o.load_id && (
      o.priority === 'asap'
      || (o.despatch_method === 'collection' && o.collection_at && formatDateTimeForInput(o.collection_at, tz).slice(0, 10) <= today)
    )),
    [orders, tz, today],
  );
  const nowIds = useMemo(() => new Set(despatchNow.map((o) => o.id)), [despatchNow]);

  const visible = useMemo(
    () => orders.filter((o) => !nowIds.has(o.id) && (showOnLoad || !o.load_id)),
    [orders, nowIds, showOnLoad],
  );
  const groups = useMemo(() => groupOrders(visible), [visible]);

  const selectedOrders = [...selected].map((id) => byId.get(id)).filter((o): o is DespatchOrder => !!o);
  const selectedTrees = selectedOrders.reduce((n, o) => n + o.total_trees, 0);

  const toggle = (id: number) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGroup = (g: Group) => setSelected((s) => {
    const ids = g.orders.filter((o) => !o.load_id).map((o) => o.id);
    const all = ids.every((id) => s.has(id));
    const next = new Set(s);
    for (const id of ids) if (all) next.delete(id); else next.add(id);
    return next;
  });

  const openNewLoad = () => {
    const methods = new Set(selectedOrders.map((o) => o.despatch_method));
    const only = methods.size === 1 ? [...methods][0] : null;
    const zoneIds = [...new Set(selectedOrders.map((o) => o.delivery_zone_id).filter((z): z is number => !!z))];
    setNewLoad({
      load_type: only === 'collection' ? 'collection' : only === 'post' ? 'post' : only === 'carrier' ? 'carrier'
        : only === 'cross_dock' ? 'cross_dock' : 'run',
      despatch_farm_id: data?.farms.length === 1 ? data.farms[0].id : 0,
      despatch_date: from > today ? from : today,
      zone_ids: zoneIds,
    });
  };

  const addToExisting = async () => {
    if (!targetLoadId || !selected.size) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.addOrdersToLoad(targetLoadId, [...selected]);
      if (res.success) {
        setTargetLoadId('');
        await load();
      } else setError(errorMessage(res, 'Could not add the orders'));
    } catch (e) {
      setError(errorMessage(e, 'Could not add the orders'));
    } finally {
      setBusy(false);
    }
  };

  const despatchToday = async () => {
    if (!nowFarmId || !despatchNow.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.addToDailyLoads(despatchNow.map((o) => o.id), today, nowFarmId);
      if (res.success) await load();
      else setError(errorMessage(res, "Could not put them on today's loads"));
    } catch (e) {
      setError(errorMessage(e, "Could not put them on today's loads"));
    } finally {
      setBusy(false);
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Manager license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Planning despatch requires a <span className="font-medium">Nursery Manager</span> or{' '}
            <span className="font-medium">Nursery Admin</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to nursery
          </Button>
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const openLoads = data?.open_loads ?? [];

  return (
    <>
      <PageHeader
        title="Despatch planner"
        subtitle="Every order due in the window, by zone. Put them on loads so nobody is missed."
        icon={<RouteIcon className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/loads')}>
            <Truck className="h-4 w-4" /> Loads
          </Button>
        }
      />
      <div className={cn('max-w-5xl mx-auto px-4 py-6 space-y-5', selected.size > 0 && 'pb-40')}>
        {/* Window */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="plan-from" className="block text-xs font-medium text-neutral-500 mb-1">From</label>
            <input id="plan-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label htmlFor="plan-to" className="block text-xs font-medium text-neutral-500 mb-1">To</label>
            <input id="plan-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)}
              className="border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((d) => (
              <button key={d} type="button" onClick={() => { setFrom(today); setTo(addDays(today, d)); }}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50">
                Next {d} days
              </button>
            ))}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading && !data ? (
          <div className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
        ) : s && (
          <>
            {/* The banner */}
            {s.not_on_load > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-1">
                <p className="flex items-start gap-2 font-semibold">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
                  <span>
                    {s.not_on_load} order{s.not_on_load === 1 ? '' : 's'} ({s.not_on_load_trees.toLocaleString()} trees) due
                    by {formatLocalDateInTz(to, tz)} {s.not_on_load === 1 ? 'is' : 'are'} not on a load.
                  </span>
                </p>
                {s.overdue_not_on_load > 0 && (
                  <p className="pl-7 text-red-700 font-medium">{s.overdue_not_on_load} of them should already have gone.</p>
                )}
                {s.undated > 0 && (
                  <p className="pl-7">{s.undated} order{s.undated === 1 ? ' has' : 's have'} no despatch date at all, so {s.undated === 1 ? 'it shows' : 'they show'} in every window.</p>
                )}
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>Every order due by {formatLocalDateInTz(to, tz)} is on a load.</span>
              </div>
            )}
            {s.on_load_unallocated > 0 && (
              <p className="text-sm text-amber-800">
                {s.on_load_unallocated} order{s.on_load_unallocated === 1 ? ' is' : 's are'} on a load but not fully allocated.
              </p>
            )}

            {/* Despatch now */}
            {despatchNow.length > 0 && (
              <section className="bg-white border border-orange-200 rounded-xl p-4" aria-labelledby="despatch-now">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h2 id="despatch-now" className="flex items-center gap-2 text-sm font-semibold text-orange-800 uppercase tracking-wide">
                    <Zap className="h-4 w-4" /> Despatch now ({despatchNow.length})
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto min-w-0">
                    {(data?.farms.length ?? 0) > 1 && (
                      <select aria-label="Despatch from" value={nowFarmId} onChange={(e) => setNowFarmId(e.target.value ? Number(e.target.value) : '')}
                        className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm w-full sm:w-64 min-w-0">
                        <option value="">From which farm?</option>
                        {data!.farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    )}
                    <Button size="sm" onClick={despatchToday} disabled={busy || !nowFarmId}>
                      Put on today's loads
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 mb-3">
                  ASAP orders and collections booked for today. Each goes on today's standing load for how it is sent: post, carrier, collections, or a local ad hoc delivery.
                </p>
                <ul className="divide-y divide-neutral-100">
                  {despatchNow.map((o) => <OrderRow key={o.id} order={o} tz={tz} />)}
                </ul>
              </section>
            )}

            <label className="flex items-center gap-2 text-sm text-neutral-600">
              <input type="checkbox" checked={showOnLoad} onChange={(e) => setShowOnLoad(e.target.checked)} />
              Also show orders already on a load ({s.on_load})
            </label>

            {groups.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">
                {orders.length ? 'Everything due in this window is on a load.' : 'No open orders are due in this window.'}
              </p>
            ) : groups.map((g) => {
              const selectable = g.orders.filter((o) => !o.load_id);
              const allOn = selectable.length > 0 && selectable.every((o) => selected.has(o.id));
              const trees = g.orders.reduce((n, o) => n + o.total_trees, 0);
              return (
                <section key={g.key} className="bg-white border border-neutral-200 rounded-xl" aria-label={g.title}>
                  <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 border-b border-neutral-100">
                    <input
                      type="checkbox"
                      aria-label={`Select all in ${g.title}`}
                      checked={allOn}
                      disabled={!selectable.length}
                      onChange={() => toggleGroup(g)}
                    />
                    <h2 className="font-semibold text-neutral-900 break-words min-w-0 flex-1">{g.title}</h2>
                    <span className="text-xs text-neutral-500">
                      {g.orders.length} order{g.orders.length === 1 ? '' : 's'} · {trees.toLocaleString()} trees
                    </span>
                  </header>
                  <ul className="divide-y divide-neutral-100">
                    {g.orders.map((o) => (
                      <OrderRow key={o.id} order={o} tz={tz} selected={selected.has(o.id)} onToggle={o.load_id ? undefined : () => toggle(o.id)} />
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 shadow-lg">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-neutral-800">
              {selected.size} selected · {selectedTrees.toLocaleString()} trees
            </span>
            <Button size="sm" onClick={openNewLoad}>New load</Button>
            {openLoads.length > 0 && (
              <div className="flex items-center gap-2 min-w-0 w-full sm:w-auto">
                <select aria-label="Add to an existing load" value={targetLoadId}
                  onChange={(e) => setTargetLoadId(e.target.value ? Number(e.target.value) : '')}
                  className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0 sm:w-72 sm:flex-none">
                  <option value="">Add to a load…</option>
                  {openLoads.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.load_number} · {LOAD_TYPE_LABELS[l.load_type]} · {formatLocalDateInTz(l.despatch_date, tz)}
                    </option>
                  ))}
                </select>
                <Button variant="outline" size="sm" onClick={addToExisting} disabled={!targetLoadId || busy}>Add</Button>
              </div>
            )}
            <button type="button" className="text-sm text-neutral-500 underline ml-auto" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {newLoad && data && (
        <NewLoadDialog
          initial={newLoad}
          orderIds={[...selected]}
          trees={selectedTrees}
          farms={data.farms}
          zones={zones}
          onClose={() => setNewLoad(null)}
          onCreated={(l) => navigate(`/nursery/loads/${l.id}`)}
        />
      )}
    </>
  );
}

function OrderRow({ order: o, tz, selected, onToggle }: {
  order: DespatchOrder; tz: string; selected?: boolean; onToggle?: () => void;
}) {
  const when = despatchWhen(o, tz);
  const short = o.allocated_trees < o.total_trees;
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      {onToggle !== undefined || o.load_id ? (
        <input
          type="checkbox"
          className="mt-1"
          aria-label={`Select ${o.order_number}`}
          checked={!!selected}
          disabled={!onToggle}
          onChange={onToggle}
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link to={`/nursery/orders/${o.id}`} className="font-medium text-primary-700 hover:underline">{o.order_number}</Link>
          <span className="text-neutral-800 break-words min-w-0">{o.customer_name}</span>
          {o.priority === 'asap' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-xs font-semibold">
              <Zap className="h-3 w-3" /> ASAP
            </span>
          )}
          {o.due_class === 'overdue' && (
            <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">Overdue</span>
          )}
          {o.due_class === 'undated' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5 text-xs font-medium">
              <CalendarX className="h-3 w-3" /> No date
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>{DESPATCH_METHOD_LABELS[o.despatch_method]}</span>
          {when && <span>{when}</span>}
          {o.despatch_method === 'cross_dock' ? (
            <span className="break-words min-w-0">
              {[o.customer_code ? `Store ${o.customer_code}` : null, o.customer_po ? `PO ${o.customer_po}` : null,
                o.xdock_del_seq ? `Del. ${o.xdock_del_seq}` : null].filter(Boolean).join(' · ')}
            </span>
          ) : o.despatch_method !== 'collection' && addressLine(o) && <span className="break-words min-w-0">{addressLine(o)}</span>}
        </div>
        {o.load_id && (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <Link to={`/nursery/loads/${o.load_id}`} className="text-primary-700 hover:underline font-medium">{o.load_number}</Link>
            {o.load_despatch_date && <span className="text-neutral-500">{formatLocalDateInTz(o.load_despatch_date, tz)}</span>}
            {o.load_status && <LoadStatusBadge status={o.load_status} />}
          </div>
        )}
      </div>
      <div className={cn('text-right text-sm whitespace-nowrap', short ? 'text-amber-700' : 'text-neutral-700')}>
        <div className="font-semibold">{o.total_trees.toLocaleString()}</div>
        <div className="text-xs">{short ? `${o.allocated_trees.toLocaleString()} allocated` : 'allocated'}</div>
      </div>
    </li>
  );
}

export default DespatchPlanner;
